// ./src/components/features/EventVendors/vendorUi.ts
//
// ROADMAP #170 — how vendor compliance READS on screen: a request's state, what needs a look, a calendar date, a person's
// name. One place, so the list, the vendor's page and the drawers say the same words.
import dayjs from "dayjs";

import { EventVendor, EventVendorPerson, EventVendorRequirement, VENDOR_REQUIREMENT_STATUS } from "@/utils/api";

export const REQUIREMENT_STATUS_LABEL: Record<string, string> = {
  [VENDOR_REQUIREMENT_STATUS.MISSING]: "Not received",
  [VENDOR_REQUIREMENT_STATUS.SUBMITTED]: "To review",
  [VENDOR_REQUIREMENT_STATUS.APPROVED]: "Approved",
  [VENDOR_REQUIREMENT_STATUS.RETURNED]: "Sent back",
};

/** Status colours are status, never brand (a returned one is the vendor's move, so orange, not red). */
export const REQUIREMENT_STATUS_COLOR: Record<string, string> = {
  [VENDOR_REQUIREMENT_STATUS.MISSING]: "gray",
  [VENDOR_REQUIREMENT_STATUS.SUBMITTED]: "blue",
  [VENDOR_REQUIREMENT_STATUS.APPROVED]: "green",
  [VENDOR_REQUIREMENT_STATUS.RETURNED]: "orange",
};

export const ATTENTION_LABEL: Record<string, string> = {
  overdue: "Overdue",
  expires: "Expires before the event",
};

/**
 * Whether the crew can upload a document as the answer to this request: only while nothing has been sent for it (review
 * 2026-10-20). ⚠ ONE gate for the review drawer's "Upload one they sent you" button AND the `…/documents/:id/upload` stage,
 * so Back after a save, or a typed address, cannot open an upload the button would not offer.
 */
export const requestTakesUpload = (r: EventVendorRequirement | null | undefined): boolean => !!r && !r.document;

/** A calendar date ('YYYY-MM-DD') as "Nov 1, 2026" — a date has no zone, so none is applied. */
export const formatVendorDay = (day?: string | null): string => (day ? dayjs(day).format("MMM D, YYYY") : "");

/** A Mantine 8 date picker's value ('YYYY-MM-DD' string, or a Date from an older caller) as 'YYYY-MM-DD', or null. */
export const pickerDay = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  return value instanceof Date && !Number.isNaN(value.getTime()) ? dayjs(value).format("YYYY-MM-DD") : null;
};

export const personName = (p?: EventVendorPerson | null): string => [p?.firstName, p?.lastName].filter(Boolean).join(" ").trim();

/** "3 of 5 approved" — or what is missing when nothing is asked yet. */
export const progressLine = (vendor: EventVendor): string => {
  const c = vendor.counts;

  if (!c || c.total === 0) {
    return "No documents asked for yet";
  }

  return `${c.approved} of ${c.total} approved`;
};

/** Does this request have a document to look at? */
export const hasDocument = (r: EventVendorRequirement): boolean => !!r.document?.id;
